import { useEffect, useState, useRef } from 'react';
import { MSA, SOW, Vendor, AuthStatus, AuthEvent } from '../types';
import { msaStore, sowStore, vendorStore } from '../services/store';
import { useVendorRole } from '../hooks/useVendorRole';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const INPUT = 'w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a5c2a]/20 focus:border-[#1a5c2a]';

const AUTH_COLOR: Record<AuthStatus, [string, string]> = {
  PendingVM:  ['#FAEEDA', '#854F0B'],
  PendingDM:  ['#E6F1FB', '#185FA5'],
  PendingTee: ['#EEEDFE', '#534AB7'],
  Executed:   ['#EAF3DE', '#3B6D11'],
  Rejected:   ['#FCEBEB', '#A32D2D'],
};
const AUTH_LABEL: Record<AuthStatus, string> = {
  PendingVM:  'Pending VM',
  PendingDM:  'Pending DM',
  PendingTee: 'Pending Tee',
  Executed:   'Executed',
  Rejected:   'Rejected',
};

function AuthBadge({ status }: { status: AuthStatus }) {
  const [bg, color] = AUTH_COLOR[status] ?? ['#F1EFE8', '#5F5E5A'];
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bg, color }}>{AUTH_LABEL[status]}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─── Document upload ──────────────────────────────────────────────────────────

function DocUpload({ currentName, onUpload }: {
  currentName?: string;
  onUpload: (base64: string, fileName: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB (localStorage limit)');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      onUpload(ev.target?.result as string, file.name);
      toast.success(`Uploaded: ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-2">
      {currentName ? (
        <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded">📎 {currentName}</span>
      ) : (
        <span className="text-xs text-gray-400">No document uploaded</span>
      )}
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="px-2 py-1 rounded border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 cursor-pointer"
      >
        {currentName ? 'Replace' : 'Upload'}
      </button>
      <input ref={ref} type="file" accept=".pdf,.doc,.docx,.png,.jpg" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ─── Auth workflow panel ──────────────────────────────────────────────────────

const TEE_EMAIL = 'tcleveland@christmaslumber.com';

function AuthWorkflow({
  contractType,
  contractId: _contractId,
  contractLabel,
  vendorName,
  authStatus,
  authHistory,
  currentUserEmail,
  onStatusChange,
  canApprove,
  isVendorManager,
}: {
  contractType: 'MSA' | 'SOW';
  contractId: string;
  contractLabel: string;
  vendorName: string;
  authStatus: AuthStatus;
  authHistory: AuthEvent[];
  currentUserEmail: string;
  onStatusChange: (status: AuthStatus, event: AuthEvent) => void;
  canApprove: boolean;
  isVendorManager: boolean;
}) {
  const [note, setNote] = useState('');
  const [showReject, setShowReject] = useState(false);

  const steps = [
    { key: 'VMReview',     label: 'VM Review',   actor: 'Vendor Manager' },
    { key: 'DMApproval',   label: 'DM Approval', actor: 'Dept Manager' },
    { key: 'TeeSignature', label: 'Tee Signs',   actor: 'Tee Cleveland' },
  ] as const;

  const stepIndex = { PendingVM: 0, PendingDM: 1, PendingTee: 2, Executed: 3, Rejected: -1 }[authStatus];

  const buildMailto = () => {
    const subject = encodeURIComponent(`[Contract Signature Request] ${vendorName} — ${contractLabel}`);
    const body = encodeURIComponent(
      `Tee,\n\nPlease review and sign the following contract.\n\nVendor: ${vendorName}\nContract: ${contractLabel} (${contractType})\n\nThis contract has been reviewed by the Vendor Manager and approved by the Department Manager and is ready for your signature.\n\nThank you,\n${currentUserEmail}`
    );
    return `mailto:${TEE_EMAIL}?subject=${subject}&body=${body}`;
  };

  const advance = (newStatus: AuthStatus, step: 'VMReview' | 'DMApproval' | 'TeeSignature', approvalStatus: 'Approved' | 'Rejected') => {
    const event: AuthEvent = {
      step,
      status: approvalStatus,
      actor: currentUserEmail,
      timestamp: new Date().toISOString(),
      notes: note || undefined,
    };
    onStatusChange(newStatus, event);
    setNote('');
    setShowReject(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">Authorization Workflow</div>

      {/* Step indicators */}
      <div className="flex items-center gap-0 mb-4">
        {steps.map((step, i) => {
          const done = stepIndex > i;
          const active = stepIndex === i;
          const rejected = authStatus === 'Rejected';
          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                rejected && active ? 'bg-[#FCEBEB] text-[#A32D2D]' :
                done    ? 'bg-[#1a5c2a] text-white' :
                active  ? 'bg-[#E6F1FB] text-[#185FA5] border-2 border-[#185FA5]' :
                          'bg-gray-100 text-gray-400'
              }`}>{done ? '✓' : i + 1}</div>
              <div className="ml-1.5 flex-1">
                <div className="text-[11px] font-medium text-gray-700">{step.label}</div>
                <div className="text-[10px] text-gray-400">{step.actor}</div>
              </div>
              {i < steps.length - 1 && <div className="w-4 h-px bg-gray-200 mx-1 shrink-0" />}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      {authStatus === 'PendingVM' && isVendorManager && (
        <button
          onClick={() => advance('PendingDM', 'VMReview', 'Approved')}
          className="px-3 py-1.5 bg-[#1a5c2a] text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-[#154a22]"
        >
          Mark Ready for DM Review →
        </button>
      )}

      {authStatus === 'PendingDM' && canApprove && (
        <div className="space-y-2">
          {showReject ? (
            <>
              <textarea
                className={`${INPUT} min-h-[56px] resize-y`}
                placeholder="Rejection reason (optional)…"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              <div className="flex gap-2">
                <button onClick={() => advance('PendingVM', 'DMApproval', 'Rejected')} className="px-3 py-1.5 bg-[#FCEBEB] text-[#A32D2D] rounded-lg text-xs font-medium cursor-pointer hover:bg-[#F7C1C1]">
                  Confirm Rejection
                </button>
                <button onClick={() => setShowReject(false)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs cursor-pointer">
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => advance('PendingTee', 'DMApproval', 'Approved')} className="px-3 py-1.5 bg-[#1a5c2a] text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-[#154a22]">
                Approve → Send to Tee
              </button>
              <button onClick={() => setShowReject(true)} className="px-3 py-1.5 bg-[#FCEBEB] text-[#A32D2D] rounded-lg text-xs font-medium cursor-pointer hover:bg-[#F7C1C1]">
                Reject
              </button>
            </div>
          )}
        </div>
      )}

      {authStatus === 'PendingTee' && isVendorManager && (
        <div className="flex flex-col gap-2">
          <a
            href={buildMailto()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#EEEDFE] text-[#534AB7] rounded-lg text-xs font-medium hover:bg-[#CECBF6]"
          >
            📧 Open Outlook draft for Tee →
          </a>
          <button
            onClick={() => advance('Executed', 'TeeSignature', 'Approved')}
            className="w-fit px-3 py-1.5 bg-[#EAF3DE] text-[#3B6D11] rounded-lg text-xs font-medium cursor-pointer hover:bg-[#C0DD97]"
          >
            Mark as Executed (Tee signed)
          </button>
        </div>
      )}

      {authStatus === 'Executed' && (
        <div className="text-xs text-[#3B6D11] bg-[#EAF3DE] px-3 py-1.5 rounded-lg">
          ✓ Contract fully executed
        </div>
      )}

      {authStatus === 'Rejected' && (
        <div className="text-xs text-[#A32D2D] bg-[#FCEBEB] px-3 py-1.5 rounded-lg">
          ✗ Rejected — returned to Vendor Manager for revision
        </div>
      )}

      {/* History */}
      {authHistory.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">History</div>
          {authHistory.map((ev, i) => (
            <div key={i} className="flex gap-2 text-[11px] text-gray-500 mb-1">
              <span className="text-gray-300">{ev.timestamp.split('T')[0]}</span>
              <span>{ev.step}</span>
              <span className={ev.status === 'Approved' ? 'text-green-700' : 'text-red-600'}>{ev.status}</span>
              <span>by {ev.actor}</span>
              {ev.notes && <span className="text-gray-400">— {ev.notes}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MSA form ─────────────────────────────────────────────────────────────────

function MSAModal({ msa, vendors, onSave, onCancel }: {
  msa?: MSA; vendors: Vendor[]; onSave: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    vendorId:       msa?.vendorId       ?? '',
    effectiveDate:  msa?.effectiveDate  ?? '',
    expirationDate: msa?.expirationDate ?? '',
    autoRenewal:    msa?.autoRenewal    ?? false,
    notes:          msa?.notes          ?? '',
  });
  const [doc, setDoc] = useState<{ base64: string; name: string } | undefined>(
    msa?.documentBase64 ? { base64: msa.documentBase64, name: msa.documentFileName ?? 'document' } : undefined
  );

  const save = () => {
    if (!form.vendorId) { toast.error('Select a vendor'); return; }
    const payload = {
      vendorId:         form.vendorId,
      effectiveDate:    form.effectiveDate || undefined,
      expirationDate:   form.expirationDate || undefined,
      autoRenewal:      form.autoRenewal,
      documentBase64:   doc?.base64,
      documentFileName: doc?.name,
      authStatus:       (msa?.authStatus ?? 'PendingVM') as AuthStatus,
      notes:            form.notes || undefined,
    };
    if (msa) {
      msaStore.update(msa.id, payload);
      toast.success('MSA updated');
    } else {
      msaStore.create(payload);
      toast.success('MSA created — authorization workflow started');
    }
    onSave();
  };

  return (
    <>
      <h2 className="text-[17px] font-semibold text-gray-900 mb-4">{msa ? 'Edit MSA' : 'New MSA'}</h2>
      {msa ? (
        <p className="text-xs text-gray-400 mb-4">Vendor: {vendors.find(v => v.id === form.vendorId)?.name ?? '—'}</p>
      ) : (
        <Field label="Vendor *">
          <select className={INPUT} value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value }))}>
            <option value="">— Select vendor —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Effective Date">
          <input className={INPUT} type="date" value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} />
        </Field>
        <Field label="Expiration Date">
          <input className={INPUT} type="date" value={form.expirationDate} onChange={e => setForm(f => ({ ...f, expirationDate: e.target.value }))} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700 mb-3 cursor-pointer">
        <input type="checkbox" checked={form.autoRenewal} onChange={e => setForm(f => ({ ...f, autoRenewal: e.target.checked }))} />
        Auto-renewal
      </label>
      <Field label="Document">
        <DocUpload currentName={doc?.name} onUpload={(b, n) => setDoc({ base64: b, name: n })} />
      </Field>
      <Field label="Notes">
        <textarea className={`${INPUT} min-h-[56px] resize-y`} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </Field>
      <div className="flex justify-end gap-2.5 mt-4">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm cursor-pointer">Cancel</button>
        <button onClick={save} className="px-4 py-2 rounded-lg bg-[#1a5c2a] text-white text-sm font-medium cursor-pointer hover:bg-[#154a22]">Save MSA</button>
      </div>
    </>
  );
}

// ─── SOW form ─────────────────────────────────────────────────────────────────

function SOWModal({ sow, vendors, onSave, onCancel }: {
  sow?: SOW; vendors: Vendor[]; onSave: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    vendorId:    sow?.vendorId    ?? '',
    msaId:       sow?.msaId      ?? '',
    sowNumber:   sow?.sowNumber   ?? '',
    name:        sow?.name        ?? '',
    description: sow?.description ?? '',
    startDate:   sow?.startDate   ?? '',
    endDate:     sow?.endDate     ?? '',
    dollarValue: sow?.dollarValue?.toString() ?? '',
    budgetAmount: sow?.budget?.amount?.toString() ?? '',
    budgetType:  sow?.budget?.type ?? 'PerContract' as const,
    notes:       sow?.notes       ?? '',
  });
  const [doc, setDoc] = useState<{ base64: string; name: string } | undefined>(
    sow?.documentBase64 ? { base64: sow.documentBase64, name: sow.documentFileName ?? 'document' } : undefined
  );

  const msas = form.vendorId ? msaStore.listByVendor(form.vendorId) : [];

  const save = () => {
    if (!form.vendorId) { toast.error('Select a vendor'); return; }
    if (!form.sowNumber.trim()) { toast.error('SOW number is required'); return; }
    if (!form.name.trim()) { toast.error('SOW name is required'); return; }
    const payload = {
      vendorId:         form.vendorId,
      msaId:            form.msaId || undefined,
      sowNumber:        form.sowNumber.trim(),
      name:             form.name.trim(),
      description:      form.description || undefined,
      startDate:        form.startDate || undefined,
      endDate:          form.endDate || undefined,
      dollarValue:      form.dollarValue ? parseFloat(form.dollarValue) : undefined,
      budget:           form.budgetAmount ? { amount: parseFloat(form.budgetAmount), type: form.budgetType } : undefined,
      documentBase64:   doc?.base64,
      documentFileName: doc?.name,
      authStatus:       (sow?.authStatus ?? 'PendingVM') as AuthStatus,
      notes:            form.notes || undefined,
    };
    if (sow) {
      sowStore.update(sow.id, payload);
      toast.success('SOW updated');
    } else {
      sowStore.create(payload);
      toast.success('SOW created — authorization workflow started');
    }
    onSave();
  };

  return (
    <>
      <h2 className="text-[17px] font-semibold text-gray-900 mb-4">{sow ? 'Edit SOW' : 'New SOW'}</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vendor *">
          <select className={INPUT} value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value, msaId: '' }))}>
            <option value="">— Select vendor —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Linked MSA (optional)">
          <select className={INPUT} value={form.msaId} onChange={e => setForm(f => ({ ...f, msaId: e.target.value }))}>
            <option value="">— None —</option>
            {msas.map(m => <option key={m.id} value={m.id}>MSA {m.effectiveDate ? `(eff. ${m.effectiveDate})` : m.id.slice(0,8)}</option>)}
          </select>
        </Field>
        <Field label="SOW Number *">
          <input className={INPUT} value={form.sowNumber} onChange={e => setForm(f => ({ ...f, sowNumber: e.target.value }))} placeholder="SOW-2026-001" />
        </Field>
        <Field label="SOW Name *">
          <input className={INPUT} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </Field>
      </div>
      <Field label="Description">
        <textarea className={`${INPUT} min-h-[56px] resize-y`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start Date"><input className={INPUT} type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} /></Field>
        <Field label="End Date"><input className={INPUT} type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} /></Field>
        <Field label="Dollar Value ($)"><input className={INPUT} type="number" min="0" value={form.dollarValue} onChange={e => setForm(f => ({ ...f, dollarValue: e.target.value }))} /></Field>
        <Field label="Budget ($)"><input className={INPUT} type="number" min="0" value={form.budgetAmount} onChange={e => setForm(f => ({ ...f, budgetAmount: e.target.value }))} /></Field>
      </div>
      <Field label="Document">
        <DocUpload currentName={doc?.name} onUpload={(b, n) => setDoc({ base64: b, name: n })} />
      </Field>
      <Field label="Notes">
        <textarea className={`${INPUT} min-h-[44px] resize-y`} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </Field>
      <div className="flex justify-end gap-2.5 mt-4">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm cursor-pointer">Cancel</button>
        <button onClick={save} className="px-4 py-2 rounded-lg bg-[#1a5c2a] text-white text-sm font-medium cursor-pointer hover:bg-[#154a22]">Save SOW</button>
      </div>
    </>
  );
}

// ─── MSA row ──────────────────────────────────────────────────────────────────

function MSARow({ msa, vendor, onEdit, onRefresh, role, userEmail }: {
  msa: MSA; vendor: Vendor; onEdit: () => void; onRefresh: () => void;
  role: string; userEmail: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const isVM = role === 'VendorManager';
  const isDM = role === 'DeptManager';

  const handleStatusChange = (status: AuthStatus, event: AuthEvent) => {
    const history = [...msa.authHistory, event];
    msaStore.update(msa.id, { authStatus: status, authHistory: history });
    onRefresh();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-2">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(x => !x)}>
        <span className="text-[10px] text-gray-400 transition-transform duration-200 inline-block" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[14px]">{vendor.name}</span>
            <span className="text-xs text-gray-400">MSA</span>
            <AuthBadge status={msa.authStatus} />
            {msa.autoRenewal && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium">Auto-renewal</span>}
          </div>
          <div className="flex gap-4 text-[11px] text-gray-400 mt-0.5 flex-wrap">
            {msa.effectiveDate && <span>Effective: {msa.effectiveDate}</span>}
            {msa.expirationDate && <span>Expires: {msa.expirationDate}</span>}
            {msa.documentFileName && <span>📎 {msa.documentFileName}</span>}
          </div>
        </div>
        {isVM && (
          <button onClick={e => { e.stopPropagation(); onEdit(); }} className="px-2.5 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50 cursor-pointer">
            Edit
          </button>
        )}
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {msa.notes && <p className="text-sm text-gray-600 mt-3 mb-0">{msa.notes}</p>}
          <AuthWorkflow
            contractType="MSA"
            contractId={msa.id}
            contractLabel={`MSA (eff. ${msa.effectiveDate ?? 'TBD'})`}
            vendorName={vendor.name}
            authStatus={msa.authStatus}
            authHistory={msa.authHistory}
            currentUserEmail={userEmail}
            onStatusChange={handleStatusChange}
            canApprove={isDM}
            isVendorManager={isVM}
          />
        </div>
      )}
    </div>
  );
}

// ─── SOW row ──────────────────────────────────────────────────────────────────

function SOWRow({ sow, vendor, onEdit, onRefresh, role, userEmail }: {
  sow: SOW; vendor: Vendor; onEdit: () => void; onRefresh: () => void;
  role: string; userEmail: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const isVM = role === 'VendorManager';
  const isDM = role === 'DeptManager';
  const fmt = (n?: number) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n) : '—';

  const handleStatusChange = (status: AuthStatus, event: AuthEvent) => {
    const history = [...sow.authHistory, event];
    sowStore.update(sow.id, { authStatus: status, authHistory: history });
    onRefresh();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-2">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(x => !x)}>
        <span className="text-[10px] text-gray-400 transition-transform duration-200 inline-block" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[14px]">{vendor.name}</span>
            <span className="text-xs text-gray-400">{sow.sowNumber}</span>
            <span className="text-xs text-gray-600">{sow.name}</span>
            <AuthBadge status={sow.authStatus} />
          </div>
          <div className="flex gap-4 text-[11px] text-gray-400 mt-0.5 flex-wrap">
            {sow.startDate && <span>Start: {sow.startDate}</span>}
            {sow.endDate && <span>End: {sow.endDate}</span>}
            {sow.dollarValue != null && <span>Value: {fmt(sow.dollarValue)}</span>}
            {sow.documentFileName && <span>📎 {sow.documentFileName}</span>}
          </div>
        </div>
        {isVM && (
          <button onClick={e => { e.stopPropagation(); onEdit(); }} className="px-2.5 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50 cursor-pointer">
            Edit
          </button>
        )}
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {sow.description && <p className="text-sm text-gray-600 mt-3">{sow.description}</p>}
          {sow.budget && (
            <p className="text-xs text-gray-500 mt-1">Budget: {fmt(sow.budget.amount)} ({sow.budget.type})</p>
          )}
          <AuthWorkflow
            contractType="SOW"
            contractId={sow.id}
            contractLabel={`${sow.sowNumber} — ${sow.name}`}
            vendorName={vendor.name}
            authStatus={sow.authStatus}
            authHistory={sow.authHistory}
            currentUserEmail={userEmail}
            onStatusChange={handleStatusChange}
            canApprove={isDM}
            isVendorManager={isVM}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ContractsPage() {
  const { role, canManageContracts } = useVendorRole();
  const { account } = useAuth();
  const userEmail = account?.username ?? '';

  const [tab, setTab] = useState<'msas' | 'sows'>('msas');
  const [msas, setMsas] = useState<MSA[]>([]);
  const [sows, setSows] = useState<SOW[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filterAuth, setFilterAuth] = useState<string>('');
  const [showMSAModal, setShowMSAModal] = useState<{ msa?: MSA } | null>(null);
  const [showSOWModal, setShowSOWModal] = useState<{ sow?: SOW } | null>(null);

  const load = () => {
    setMsas(msaStore.list());
    setSows(sowStore.list());
    setVendors(vendorStore.list());
  };

  useEffect(() => { load(); }, []);

  if (!canManageContracts && role !== 'DeptManager') {
    return <div className="p-8 text-gray-500">You don't have access to the Contracts module.</div>;
  }

  const vendorMap = new Map(vendors.map(v => [v.id, v]));

  const filteredMSAs = msas.filter(m => !filterAuth || m.authStatus === filterAuth);
  const filteredSOWs = sows.filter(s => !filterAuth || s.authStatus === filterAuth);

  const authOptions: AuthStatus[] = ['PendingVM', 'PendingDM', 'PendingTee', 'Executed', 'Rejected'];

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 mb-0.5">Contracts</h1>
          <p className="text-sm text-gray-500">{msas.length} MSAs · {sows.length} SOWs</p>
        </div>
        {role === 'VendorManager' && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (vendors.length === 0) { toast.error('Add a vendor first'); return; }
                setShowMSAModal({});
              }}
              className="px-3 py-1.5 bg-[#1a5c2a] text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-[#154a22]"
            >
              + New MSA
            </button>
            <button
              onClick={() => setShowSOWModal({})}
              className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
            >
              + New SOW
            </button>
          </div>
        )}
      </div>

      {/* Tabs + filter */}
      <div className="flex items-center gap-4 mb-5">
        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          {(['msas', 'sows'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium cursor-pointer transition-colors ${
                tab === t ? 'bg-[#1a5c2a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t === 'msas' ? 'MSAs' : 'SOWs'}
            </button>
          ))}
        </div>
        <select
          value={filterAuth}
          onChange={e => setFilterAuth(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none"
        >
          <option value="">All statuses</option>
          {authOptions.map(s => <option key={s} value={s}>{AUTH_LABEL[s]}</option>)}
        </select>
      </div>

      {/* List */}
      {tab === 'msas' && (
        filteredMSAs.length === 0
          ? <div className="text-center py-12 text-gray-400 text-sm">No MSAs yet.</div>
          : filteredMSAs.map(msa => {
              const vendor = vendorMap.get(msa.vendorId);
              if (!vendor) return null;
              return (
                <MSARow
                  key={msa.id}
                  msa={msa}
                  vendor={vendor}
                  role={role}
                  userEmail={userEmail}
                  onEdit={() => setShowMSAModal({ msa })}
                  onRefresh={load}
                />
              );
            })
      )}
      {tab === 'sows' && (
        filteredSOWs.length === 0
          ? <div className="text-center py-12 text-gray-400 text-sm">No SOWs yet.</div>
          : filteredSOWs.map(sow => {
              const vendor = vendorMap.get(sow.vendorId);
              if (!vendor) return null;
              return (
                <SOWRow
                  key={sow.id}
                  sow={sow}
                  vendor={vendor}
                  role={role}
                  userEmail={userEmail}
                  onEdit={() => setShowSOWModal({ sow })}
                  onRefresh={load}
                />
              );
            })
      )}

      {/* Modals */}
      {(showMSAModal || showSOWModal) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setShowMSAModal(null); setShowSOWModal(null); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[560px] p-6 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
            {showMSAModal && (
              <MSAModal
                msa={showMSAModal.msa}
                vendors={vendors}
                onSave={() => { setShowMSAModal(null); load(); }}
                onCancel={() => setShowMSAModal(null)}
              />
            )}
            {showSOWModal && !showMSAModal && (
              <SOWModal
                sow={showSOWModal.sow}
                vendors={vendors}
                onSave={() => { setShowSOWModal(null); load(); }}
                onCancel={() => setShowSOWModal(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
